import React, { useEffect, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { Layout, Menu } from 'antd';
import _ from 'lodash';
import { CreditsModal } from '../../components/CreditsModal';
import { HelpModal } from '../../components/HelpModal';
import { HintsModal } from '../../components/HintsModal';
import { NotesModal } from '../../components/NotesModal';
import { QuickStartModal } from '../../components/QuickStartModal';
import { SIMULATION_COUNTRY_STATES } from '../../data';
import ApiService from '../../services/ApiService';
import DataService from '../../services/DataService';
import SessionService from '../../services/SessionService';
import SocketService from '../../services/SocketService';
import { DATA_LIST, SIDER_MENU_ITEMS } from './data';
import { ContentLayout, MainLayout, StyledContent, StyledSider } from './styles';
import { AppHeader } from './AppHeader';
import { DataLoader } from './DataLoader';

export const Portal = () => {
  const username = SessionService.getUsername();
  const [isQuickStartModalOpen, setQuickStartModalOpen] = useState(false);
  const [isHelpModalOpen, setHelpModalOpen] = useState(false);
  const [isHintsModalOpen, setHintsModalOpen] = useState(false);
  const [isCreditsModalOpen, setCreditsModalOpen] = useState(false);
  const [isNotesModalOpen, setNotesModalOpen] = useState(false);
  const [isDataLoaded, setDataLoaded] = useState(!!DataService.getItem(DATA_LIST[DATA_LIST.length - 1].key));
  const [siderMenuItems, setSiderMenuItems] = useState(SIDER_MENU_ITEMS);
  const [scList, setSCList] = useState([]);
  const [socketMessage, setSocketMessage] = useState(null);

  const handleCompleteDataLoading = () => {
    setDataLoaded(true);
  };

  const updateSiderMenuItems = () => {
    siderMenuItems[1].children = scList
      .filter(sc => ![SIMULATION_COUNTRY_STATES.INACTIVE].includes(sc.simulationCountryState))
      .map(sc => ({
        key: `countries-${sc.simulationCountryId}`,
        label: (
          <Link className="country" to={`/portal/countries/${sc.simulationCountryId}/summary`}>
            <h3>
              {sc.countryName} {sc.inUse ? '(In Use)' : ''}
            </h3>
            <div>Simulation: {sc.simulationName}</div>
            <div>Language: {sc.primaryLanguageName}</div>
            <div>Year: {sc.simulationCountryYear}</div>
          </Link>
        ),
      }));

    setSiderMenuItems([...siderMenuItems]);
  };

  const fetchSimulationCountries = async () => {
    const data = await ApiService.retrieveAccountSimulationCountryList();
    setSCList(data.resultList);
  };

  useEffect(() => {
    fetchSimulationCountries();

    SocketService.on('start-playing-sc', simulationCountryId => {
      console.log('start-playing-sc', scList.length, simulationCountryId);
      setSocketMessage({
        event: 'start-playing-sc',
        simulationCountryId: +simulationCountryId,
      });
    });

    SocketService.on('stop-playing-sc', simulationCountryId => {
      console.log('stop-playing-sc', scList.length, simulationCountryId);
      setSocketMessage({
        event: 'stop-playing-sc',
        simulationCountryId: +simulationCountryId,
      });
    });

    return () => {
      SocketService.off('start-playing-sc');
      SocketService.off('stop-playing-sc');
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (socketMessage) {
      if (socketMessage.event === 'start-playing-sc') {
        const simulationCountry = scList.find(sc => sc.simulationCountryId === socketMessage.simulationCountryId);
        if (simulationCountry) {
          simulationCountry.inUse = true;
          setSCList(_.cloneDeep(scList));
        }
      }
      if (socketMessage.event === 'stop-playing-sc') {
        const simulationCountry = scList.find(sc => sc.simulationCountryId === socketMessage.simulationCountryId);
        if (simulationCountry) {
          simulationCountry.inUse = false;
          setSCList(_.cloneDeep(scList));
        }
      }
      setSocketMessage(null);
    }
  }, [socketMessage]);

  useEffect(() => {
    updateSiderMenuItems();
    // eslint-disable-next-line
  }, [scList]);

  if (!isDataLoaded) {
    return <DataLoader onComplete={handleCompleteDataLoading} />;
  }

  const headerMenuItems = [
    {
      key: 'quick-start',
      label: (
        <Link onClick={() => setQuickStartModalOpen(true)}>
          Quick Start
        </Link>
      ),
    },
    {
      key: 'help',
      label: (
        <Link onClick={() => setHelpModalOpen(true)}>
          How To Play
        </Link>
      ),
    },
    {
      key: 'hints',
      label: <Link onClick={() => setHintsModalOpen(true)}>Hints</Link>,
    },
    {
      key: 'credits',
      label: <Link onClick={() => setCreditsModalOpen(true)}>Credits</Link>,
    },
    {
      key: 'notes',
      label: <Link onClick={() => setNotesModalOpen(true)}>Notes</Link>,
    },
  ];

  return (
    <MainLayout>
      <AppHeader initial={username[0]} headerMenuItems={headerMenuItems} />
      <Layout>
        <StyledSider width={320}>
          <Menu mode="inline" items={siderMenuItems} defaultOpenKeys={['manage', 'play']} />
        </StyledSider>
        <ContentLayout>
          <StyledContent>
            <Outlet />
          </StyledContent>
        </ContentLayout>
      </Layout>
      <QuickStartModal open={isQuickStartModalOpen} onClose={() => setQuickStartModalOpen(false)} />
      <HelpModal open={isHelpModalOpen} onClose={() => setHelpModalOpen(false)} />
      <HintsModal open={isHintsModalOpen} onClose={() => setHintsModalOpen(false)} />
      <CreditsModal open={isCreditsModalOpen} onClose={() => setCreditsModalOpen(false)} />
      <NotesModal open={isNotesModalOpen} onClose={() => setNotesModalOpen(false)} />
    </MainLayout>
  );
};
